import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, Subject, throwError, timer } from 'rxjs';
import { catchError, map, switchMap, takeWhile } from 'rxjs/operators';

export interface DedupActionPayload {
    rowData: any;
    entityName: string;
    propertyOrder?: string[];
}

export interface DedupApiRequest {
    merging_master_keys: number[];
    golden_fields: { [key: string]: any };
}

export interface DedupApiResponse {
    success: boolean;
    message?: string;
    data?: any;
}

export interface DedupStatusResponse {
  date: string;
  masterTable: string;
  goldenKey: number;
  merging_keys: number[];
  golden_fields: { [key: string]: any };
  stage: string;
  status: 'in_progress' | 'success' | 'error';
  details?: string;
}

export type DedupStatus = 'in_progress' | 'success' | 'error';

@Injectable({
    providedIn: 'root',
})
export class DedupService {
    private addToDedupSource = new Subject<DedupActionPayload>();

    // Observable stream for components to subscribe to
    public addToDedup$ = this.addToDedupSource.asObservable();

    constructor(private http: HttpClient) {}

    public triggerAddToDedup(rowData: any, entityName: string, propertyOrder?: string[]): void {
        this.addToDedupSource.next({ rowData, entityName, propertyOrder });
    }

 /**
   * Format date to MM-DD-YYYY format for API
   */
  private formatDateForApi(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  /**
   * Perform deduplication POST call
   * POST /dedupe/{date}/{masterTable}/{goldenKey}
   */
  public performDedup(date: Date, masterTable: string, goldenKey: number, request: DedupApiRequest): Observable<DedupApiResponse> {
    const baseUrl = environment.baseUrls.api;
    const dateStr = this.formatDateForApi(date);
    const url = `${baseUrl}dedupe/${dateStr}/${masterTable}/${goldenKey}`;
    
    console.log('Sending dedup request to:', url);
    console.log('Request payload:', request);
    
    return this.http.post<DedupApiResponse>(url, request).pipe(
      map((response: any) => {
        console.log('Dedup API response:', response);
        return {
          success: true,
          data: response
        };
      }),
      catchError((error) => {
        console.error('Dedup API error:', error);
        return throwError(() => new Error(`Dedup API request failed: ${error.message || 'Unknown error'}`));
      })
    );
  }

 /**
   * Get deduplication status
   * GET /dedupe/{date}/{masterTable}/{goldenKey}
   */
  public getDedupStatus(date: Date, masterTable: string, goldenKey: number): Observable<DedupStatusResponse> {
    const baseUrl = environment.baseUrls.api;
    const dateStr = this.formatDateForApi(date);
    const url = `${baseUrl}dedupe/${dateStr}/${masterTable}/${goldenKey}`;
    
    console.log('Checking dedup status:', url);
    
    return this.http.get<DedupStatusResponse>(url).pipe(
      map((response: any) => {
        console.log('Dedup status response:', response);
        return response;
      }),
      catchError((error) => {
        console.error('Dedup status check error:', error);
        return throwError(() => new Error(`Dedup status check failed: ${error.message || 'Unknown error'}`));
      })
    );
  }

  /**
   * Poll deduplication status every 4 seconds until status is 'success' or 'error'
   * @param date Date for the deduplication
   * @param masterTable Master table name
   * @param goldenKey Golden key
   * @param maxAttempts Maximum number of polling attempts (default: 60, which is 4 minutes)
   * @returns Observable that emits status updates and completes when status is 'success' or 'error'
   */
  public pollDedupStatus(
    date: Date,
    masterTable: string,
    goldenKey: number,
    maxAttempts: number = 60
  ): Observable<DedupStatusResponse> {
    let attempts = 0;
    
    return timer(0, 4000).pipe(
      switchMap(() => {
        attempts++;
        console.log(`Polling dedup status (attempt ${attempts}/${maxAttempts})...`);
        
        return this.getDedupStatus(date, masterTable, goldenKey);
      }),
      takeWhile((response: DedupStatusResponse) => {
        const shouldContinue = response.status === 'in_progress' && attempts < maxAttempts;
        if (!shouldContinue) {
          console.log(`Polling stopped. Status: ${response.status}, Attempts: ${attempts}`);
        }
        return shouldContinue;
      }, true), // inclusive: emit the last value that fails the condition
      catchError((error) => {
        console.error('Polling error:', error);
        return throwError(() => new Error(`Dedup polling failed: ${error.message || 'Unknown error'}`));
      })
    );
  }
}
